import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import DashboardTable from "./DashboardTable";
import { OrderColumns } from "./orderColumns";
import { getDashboard } from "../../services/dashboardService";
import { getOrders } from "../../services/orderService";
import {
  faPrint,
  faChartLine,
  faClipboardList,
  faFileInvoiceDollar,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faDollarSign,
  faBoxOpen,
  faReceipt,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getPoolCoCReport } from "../../services/reportService";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import "./ClientDashboard.css";

export default function ClientDashboard({ accountNumber, token }) {
  const history = useHistory();
  const [dashboard, setDashboard] = useState({
    openOrders: 0,
    openInvoices: 0,
    outstandingBalance: 0,
    totalOrders: 0,
    name: "",
    completedOrders: 0,
    pendingOrders: 0,
    totalInvoiceAmount: 0,
    avgOrderValue: 0,
    paymentStatus: {
      overdue: 0,
      dueSoon: 0,
      paid: 0,
    },
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function GetDashboardInfo() {
      try {
        let data = await getDashboard(accountNumber);
        setDashboard(data);
      } catch (err) {
        console.error(err);
      }
    }
    if (accountNumber) {
      GetDashboardInfo();
    }
  }, [accountNumber]);

  const handlePrintClick = async () => {
    try {
      setLoading(true);
      await getPoolCoCReport(accountNumber);
      NotificationManager.success("Pool CoC report saved successfully");
    } catch (error) {
      NotificationManager.error("Error while getting Pool CoC report");
    }
    setLoading(false);
  };

  // Handle stat card clicks
  const handleStatCardClick = (cardType) => {
    switch (cardType) {
      case 'openOrders':
      case 'totalOrders':
        // Scroll to Orders table below
        scrollToOrdersTable();
        break;
      case 'openInvoices':
      case 'overdue':
      case 'dueSoon':
      case 'paid':
        // Navigate to invoices page
        history.push('/invoices');
        break;
      case 'bottleOrders':
        // Navigate to bottle orders page
        history.push('/bottle-orders');
        break;
      default:
        break;
    }
  };

  const scrollToOrdersTable = () => {
    // Find the orders table and scroll to it smoothly
    setTimeout(() => {
      const ordersSection = document.querySelector('[data-testid="orders-table"]') || 
                           document.querySelector('.dashboard-table') ||
                           document.querySelector('h2:contains("Orders")');
      if (ordersSection) {
        ordersSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const orderColumns = React.useMemo(() => OrderColumns, []);

  if (!accountNumber)
    return (
      <div className="no-account-container">
        <div className="no-account-card">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="warning-icon"
          />
          <h3>Account Selection Required</h3>
          <p>
            Please select an account that you have access to from the main
            screen to view your dashboard.
          </p>
        </div>
      </div>
    );

  return (
    <>
      {/* Welcome Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="welcome-section">
            <h1 className="welcome-title">
              Welcome, {dashboard.name}
            </h1>
            <p className="welcome-subtitle">Here's what's happening with your account</p>
          </div>
          {token?.clientType?.toLowerCase().includes("pool") && (
            <div className="header-actions">
              <button
                className="print-coc-btn"
                onClick={handlePrintClick}
                disabled={loading}
              >
                {loading ? (
                  <CommonLoader loading={loading} />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPrint} />
                    <span>Print Pool CoC</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        <div className="header-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-stats">
        <div className="stats-grid">
          <div 
            className="stat-card primary clickable" 
            onClick={() => handleStatCardClick('openOrders')}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">
              <FontAwesomeIcon icon={faClipboardList} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{dashboard.openOrders}</div>
              <div className="stat-label">Open Orders</div>
              <div className="stat-sublabel">Currently in progress</div>
            </div>
            <div className="stat-trend">
              <div className="trend-indicator positive">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
            </div>
          </div>

          <div 
            className="stat-card info clickable" 
            onClick={() => handleStatCardClick('openInvoices')}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">
              <FontAwesomeIcon icon={faFileInvoiceDollar} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{dashboard.openInvoices}</div>
              <div className="stat-label">Open Invoices</div>
              <div className="stat-sublabel">
                ${dashboard.outstandingBalance?.toFixed(2)} outstanding
              </div>
            </div>
            <div className="stat-trend">
              <div className="trend-indicator neutral">
                <FontAwesomeIcon icon={faClock} />
              </div>
            </div>
          </div>

          <div 
            className="stat-card success clickable" 
            onClick={() => handleStatCardClick('totalOrders')}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{dashboard.totalOrders}</div>
              <div className="stat-label">Total Orders</div>
              <div className="stat-sublabel">All time</div>
            </div>
            <div className="stat-trend">
              <div className="trend-indicator positive">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faDollarSign} />
            </div>
            <div className="stat-content">
              <div className="stat-number">
                ${dashboard.avgOrderValue?.toFixed(2) || "0"}
              </div>
              <div className="stat-label">Avg Order Value</div>
              <div className="stat-sublabel">Per order</div>
            </div>
            <div className="stat-trend">
              <div className="trend-indicator positive">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status Cards - Now using the same style as stats cards */}
      <div className="dashboard-stats">
        <div className="stats-grid">
          <div 
            className="stat-card overdue clickable" 
            onClick={() => handleStatCardClick('overdue')}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{dashboard.paymentStatus?.overdue || 0}</div>
              <div className="stat-label">Overdue</div>
              <div className="stat-sublabel">Requires immediate attention</div>
            </div>
            <div className="stat-trend">
              <div className="trend-indicator negative">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
            </div>
          </div>

          <div 
            className="stat-card due-soon clickable" 
            onClick={() => handleStatCardClick('dueSoon')}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{dashboard.paymentStatus?.dueSoon || 0}</div>
              <div className="stat-label">Due Soon</div>
              <div className="stat-sublabel">Within 7 days</div>
            </div>
            <div className="stat-trend">
              <div className="trend-indicator neutral">
                <FontAwesomeIcon icon={faClock} />
              </div>
            </div>
          </div>

          <div 
            className="stat-card paid clickable" 
            onClick={() => handleStatCardClick('paid')}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="stat-content">
              <div className="stat-number">{dashboard.paymentStatus?.paid || 0}</div>
              <div className="stat-label">Paid</div>
              <div className="stat-sublabel">Up to date</div>
            </div>
            <div className="stat-trend">
              <div className="trend-indicator positive">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div data-testid="orders-table">
        <DashboardTable
          accountNumber={accountNumber}
          title={"Orders"}
          columns={orderColumns}
          getData={getOrders}
          color={"primary"}
        />
      </div>
    </>
  );
}

// Helper function for status classes
function getStatusClass(status) {
  switch (status?.toLowerCase()) {
    case "completed":
    case "paid":
      return "is-success";
    case "pending":
    case "processing":
      return "is-warning";
    case "cancelled":
    case "rejected":
      return "is-danger";
    default:
      return "is-info";
  }
}